import LanguageManager from "./LanguageManager.js"
import ComponentsManager from "./ComponentsManager.js";
import EventManager from "./EventManager.js";
import LoginMenu from "./modules/LoginMenu.js";
import CenterMenu from "./modules/CenterMenu.js";
import UserProfile from "./serverConnection/UserProfile.js";
import ApiService from "./serverConnection/ApiService.js";

class OverlayManager {

    /**
     * @author Corentin (ccharton) Charton
     * @description Manages the overlay UI and its interactions
     */
    constructor() {
        this.overlay = document.getElementById('overlay')
        this.isInitialized = false
        this.overlay.style.display = 'block'

        this.wheelScrollToYBind = this.wheelScrollToY.bind(this)

        this.initialize()
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Initialize the overlay by loading necessary html components and setting up event listeners
     * @returns {Promise<void>}
     */
    async initialize() {
        if(this.isInitialized) {
            return
        }

        try {
            const response = await fetch('./overlay/components/overlay.html')
            this.overlay.innerHTML = await response.text()
            this.overlay.style.display = 'block'

            this.initializeLanguageEvent()

            this.isInitialized = true
            await ComponentsManager.loadComponent('footerMenu', '#footer', './overlay/components/footerMenu.html', './overlay/styles/footerMenu.css')
            const footerScrollContainer = document.getElementById('footerScrollContainer')
            if (footerScrollContainer) {
                footerScrollContainer.addEventListener('wheel', this.wheelScrollToYBind)
            }

            const loggedIn = (await (ApiService.checkAuth())).authenticated

            if (loggedIn) {
                // A guest session survived the reload (cookies still valid): skip the
                // login menu entirely and drop the player straight back into the
                // center menu, same as a fresh continueAsGuest() would.
                UserProfile.setLoggedIn(true)
                await LoginMenu.restoreGuestSession()
            } else {
                if (document.getElementById('centerMenu')) {
                    document.getElementById('centerMenu').classList.add('hidden');
                }
                await ComponentsManager.loadComponent('loginMenu', '#centerMenuContainer', './overlay/components/loginMenu.html', './overlay/styles/loginMenu.css');
                LoginMenu.hydrate()
            }
            await LanguageManager.updateContent()

        } catch (error) {
            console.error(`Error while initializing overlay: ${error}`)
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Initialize language selection event listeners
     */
    initializeLanguageEvent() {
        document.querySelectorAll('#dropup li').forEach(item => {
            item.addEventListener('click', (event) => {
                const lang = event.target.getAttribute('lang')
                LanguageManager.changeLanguage(lang)
            })
        })
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Toggle the visibility of the overlay
     * @returns {Promise<void>}
     */
    async toggle() {
        if (this.overlay.classList.contains('hidden')) {
            if (!this.overlay.hasChildNodes()) {
                await this.initialize()
            }
            this.overlay.classList.remove('hidden')
        } else {
            this.overlay.classList.add('hidden')
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Handle horizontal scrolling for specific containers on vertical wheel events
     * @param event {WheelEvent} - The wheel event
     */
    wheelScrollToY(event) {
        const containerToScroll = event.target.closest('#tabPicker') || event.target.closest('#footerScrollContainer')

        if (containerToScroll.scrollWidth > containerToScroll.clientWidth) {
            let delta

            if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
                return
            } else {
                delta = event.deltaY
            }

            const isMac = navigator.userAgent.includes("Mac OS");
            const isChrome = navigator.userAgent.includes("Chrome");

            if (isMac && isChrome) {
                if (Math.abs(delta) < 50) {
                    delta *= 20
                }
            }

            event.preventDefault()
            containerToScroll.scrollLeft += delta
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Store the game instance for teleportation purposes
     * @param game {Game} - The game instance
     */
    storeGameInstance(game) {
        if (game) {
            this.game = game
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Reset the overlay manager and other module to their initial state
     * @returns {Promise<void>}
     */
    async reset() {
        const overlay = document.getElementById('overlay')
        if (overlay) {
            overlay.innerHTML = ''
            this.isInitialized = false
            document.querySelectorAll('link[rel="stylesheet"]').forEach(link => {
                if (!link.href.includes('index') && !link.href.includes('style.css')) {
                    link.remove();
                }
            });
            ComponentsManager.reset()
            await this.initialize()
            CenterMenu.reset()
        }
    }

}
export default new OverlayManager()